import { useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import ENDPOINT from '../constants/endpoints';
import useApi from './useApi';

const getFiltersQueryParams = sections => {
  const result = {};
  Object.keys(sections).forEach(section => {
    const { groups } = sections[section];

    Object.keys(groups).forEach(group => {
      const { name, elements = [], type = null } = groups[group];
      const groupName = name.replace(/\[\]$/, '');

      if (group.includes('candidateTags')) {
        result[groupName] = groups[group].selectedElements;
      } else if (group === 'tagType') {
        result[groupName] = groups[group].selectedElements;
      } else if (type === 'datePicker') {
        result[groupName] = groups[group].selectedElements;
      } else if (type === 'range') {
        result[groupName] = groups[group].selectedElements;
      } else {
        if (!result[groupName]) {
          result[groupName] = [];
        }

        elements.forEach(element =>
          element.selected ? result[groupName].push(element.value) : null
        );
      }
    });
  });

  return result;
};

const initSelectFilters = sections => {
  const filters = {};

  Object.keys(sections).forEach(section => {
    const {
      [section]: { groups },
    } = sections;

    Object.keys(groups).forEach(group => {
      const {
        [group]: { elements = [], selectedElements = [], limit = null },
      } = groups;

      if (limit) {
        groups[group].type = 'searchable';
      }

      if (selectedElements.length > 0) {
        elements.forEach(elem => {
          if (
            selectedElements.indexOf(elem.value) !== -1 ||
            selectedElements.indexOf(Number(elem.value)) !== -1
          ) {
            elem.selected = true;
          }
        });
      }
    });

    filters[section] = { ...sections[section] };
  });

  return filters;
};

export const useGridFilters = (filtersEndpoint, onChangeHandler, onClearHandler, onResetHandler) => {
  const [filters, setFilters] = useState();
  const api = useApi();

  const fetch = (params = {}) => {
    return new Promise((resolve, reject) => {
      api.get(filtersEndpoint, params, {
        handleError: response => reject({response, code: 0}),
        handleNotFound: response => reject({response, code: 404}),
        handleForbidden: response => reject({response, code: 403}),
        handleSuccess: response => {
          const sections = initSelectFilters(response);
          setFilters({...sections});
          resolve(getFiltersQueryParams(sections));
        },
      });
    })
  };

  const onChange = action => {
    typeof onChangeHandler === 'function' && onChangeHandler(action);
  };

  const clear = () => {
    const sections = cloneDeep(filters);
    Object.keys(sections).forEach(sectionKey => {
      const { groups } = sections[sectionKey];

      Object.keys(groups).forEach(groupKey => {
        const { elements, type } = groups[groupKey];

        if (type === 'datePicker' || type === 'range' || groupKey === 'graduation') {
          groups[groupKey].selectedElements = null;
        } else if (sectionKey === 'candidate-tags') {
          groups[groupKey].selectedElements = [];
        } else {
          elements.forEach(element => (element.selected = false));
        }
      });
    });

    if (onClearHandler) {
      onClearHandler().then((response) => {
        if (response.filters) {
          const filters = initSelectFilters(response.filters);
          setFilters({...filters});
        } else {
          setFilters({ ...sections });
        }

        onChange('clear');
      });
    } else {
      setFilters({ ...sections });
      onChange('clear');
    }
  };

  const resetToDefault = () => {
    const sections = cloneDeep(filters);
    Object.keys(sections).forEach(sectionKey => {
      const { groups } = sections[sectionKey];

      Object.keys(groups).forEach(groupKey => {
        const { elements, type } = groups[groupKey];

        if (groupKey === 'graduation') {
          const currentYear = new Date().getFullYear();
          groups[groupKey].selectedElements = [currentYear - 7, currentYear];
        } else if (type === 'datePicker' || type === 'range') {
          groups[groupKey].selectedElements = null;
        } else if (sectionKey === 'candidate-tags') {
          groups[groupKey].selectedElements = [];
        } else {
          elements.forEach(element => (element.selected = false));
        }
      });
    });

    if (onResetHandler) {
      onResetHandler().then((response) => {
        if (response.filters) {
          const filters = initSelectFilters(response.filters);
          setFilters({...filters});
        } else {
          setFilters({ ...sections });
        }

        onChange('clear');
      });
    } else {
      setFilters({ ...sections });
      onChange('clear');
    }
  };

  const change = (section, group, value) => {
    const sections = cloneDeep(filters);
    const {
      [section]: {
        groups: {
          [group]: { elements, type = null },
        },
      },
    } = sections;


    if (section === 'candidate-tags') {
      let currentValue = sections[section].groups[group].selectedElements;
      const isExclude = parseInt(value) < 0;

      if (currentValue && currentValue.includes(parseInt(value))) {
        if (!isExclude) {
          currentValue = currentValue.filter(item => item !== -value);
        }

        if (isExclude) {
          currentValue.push(Math.abs(value));
        }

        if (group === 'tagType') {
          sections[section].groups[`candidateTags-${Math.abs(value)}-group`].selectedElements = [];
        }

        sections[section].groups[group].selectedElements = currentValue.filter(item => item !== parseInt(value));
      } else {

        if (isExclude) {
          currentValue = currentValue.filter(item => item !== Math.abs(value));
        }

        if (group === 'tagType') {
          sections[section].groups[`candidateTags-${Math.abs(value)}-group`].selectedElements = [];
        }

        sections[section].groups[group].selectedElements = [...(currentValue || []), parseInt(value)];
      }
    } else if (type === 'datePicker') {
      sections[section].groups[group].selectedElements = value;
    } else if (type === 'range') {
      sections[section].groups[group].selectedElements = value;
    } else {
      elements.some(element => {
        const selectedElement = element.value === value;
        if (selectedElement) {
          element.selected = !element.selected;
        }
        return selectedElement;
      });
    }

    setFilters({ ...sections });
    onChange('change');
  };

  const push = (section, group, value) => {
    const sections = cloneDeep(filters);
    const {
      [section]: {
        groups: {
          [group]: { elements },
        },
      },
    } = sections;

    elements.push({
      ...value,
      selected: true,
    });

    setFilters({ ...sections });
    onChange('push');
  };

  const search = (value, group) => {
    return new Promise(resolve => {
      api.get(
        ENDPOINT.FILTERS.FIND,
        { value, group },
        {
          handleSuccess: ({ rows = [] }) => {
            resolve(rows.map(item => ({ ...item, label: item.text })));
            onChange('search');
          },
        }
      );
    });
  };

  const groupSelect = (section, group) => {
    const sections = cloneDeep(filters);
    const {
      [section]: {
        groups: {
          [group]: { elements },
        },
      },
    } = sections;

    if (section === 'candidate-tags') {
      sections[section].groups[group].selectedElements = sections[section].groups[group].elements.map(item => parseInt(item.value));
    } else {
      elements.forEach(element => (element.selected = true));
    }

    setFilters({ ...sections });
    onChange('groupSelect');
  };

  const groupClear = (section, group) => {
    const sections = cloneDeep(filters);
    const {
      [section]: {
        groups: {
          [group]: { elements },
        },
      },
    } = sections;

    if (section === 'candidate-tags') {

      if (group === 'tagType') {
        const groupIds = sections[section].groups[group].elements.map(item => parseInt(item.value))

        groupIds.forEach(id => {
          sections[section].groups[`candidateTags-${id}-group`].selectedElements = [];
        });

        sections[section].groups[group].selectedElements = [];
      }

      sections[section].groups[group].selectedElements = [];
    } else {
      elements.forEach(element => (element.selected = false));
    }

    setFilters({ ...sections });
    onChange('groupClear');
  };

  const getQuery = () => {
    return getFiltersQueryParams(filters);
  };

  const loadFilters = filters => {
    setFilters({ ...initSelectFilters(filters) });
  };

  return {
    data: filters,
    actions: {
      fetch,
      clear,
      resetToDefault,
      change,
      search,
      push,
      groupSelect,
      groupClear,
      getQuery,
      setFilters: loadFilters,
    },
  };
};
